/**
 * @module useFoodCost
 * @description Singleton composable managing all food-cost state, CRUD, and API calls.
 *
 * Follows the same pattern as useContacts, useWarehouse, etc. — one composable
 * manages all state so multiple components share the same reactive data.
 */

import { ref, computed, watch } from "vue";
import { exportMenuItemsNoPricesPdf } from "@/utils/foodcostPdfExport.js";
import { menuApi } from "@/utils/api.js";

// ── Singleton State ─────────────────────────────────────────────────────────

// Ingredients
const ingredients = ref([]);
const allIngredients = ref([]); // unfiltered list for recipe dialog dropdown
const ingredientCategories = ref([]);
const ingredientSearch = ref("");
const ingredientCategoryFilter = ref(null);
const ingredientLoading = ref(false);

// Recipes
const recipes = ref([]);
const allRecipes = ref([]); // unfiltered list for sub-recipe picker dropdown
const recipeCategories = ref([]);
const recipeSearch = ref("");
const recipeCategoryFilter = ref(null);
const recipeIngredientFilter = ref(null);
const recipeMenuItemFilter = ref(null);
const recipeBrandFilter = ref(null);
const recipeLoading = ref(false);

// Menu Items
const menuItems = ref([]);
const allMenuItems = ref([]); // unfiltered copy for combos, categories, etc.
const menuItemSearch = ref("");
const menuItemCategoryFilter = ref(null);
const menuItemBrandFilter = ref(null);
// Catering flag filter: true = suitable for catering, false = not, null = all
const menuItemCateringFilter = ref(null);
/** Catering types: the live stations and the buffet (2026-10-30). Keep in step with backend/menu/controllers/menu-items.js. */
export const CATERING_TYPES = ["Pizza", "Pasta", "Fried", "Coffee", "Buffet"];
// Catering type filter: one of CATERING_TYPES, null = all
const menuItemCateringTypeFilter = ref(null);

/** Choices of the Catering filter (Menu Items filter bar and mobile filter dialog) */
export const CATERING_FILTER_ITEMS = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const menuItemLoading = ref(false);

const FOODCOST_FILTERS_KEY = "foodCostFilters";

// Restore filter state from localStorage on init
const savedFcFilters = localStorage.getItem(FOODCOST_FILTERS_KEY);
if (savedFcFilters) {
  try {
    const p = JSON.parse(savedFcFilters);
    ingredientSearch.value = p.ingredientSearch ?? "";
    ingredientCategoryFilter.value = p.ingredientCategoryFilter ?? null;
    recipeSearch.value = p.recipeSearch ?? "";
    recipeCategoryFilter.value = p.recipeCategoryFilter ?? null;
    recipeIngredientFilter.value = p.recipeIngredientFilter ?? null;
    recipeMenuItemFilter.value = p.recipeMenuItemFilter ?? null;
    recipeBrandFilter.value = p.recipeBrandFilter ?? null;
    menuItemSearch.value = p.menuItemSearch ?? "";
    menuItemCategoryFilter.value = p.menuItemCategoryFilter ?? null;
    menuItemBrandFilter.value = p.menuItemBrandFilter ?? null;
    menuItemCateringFilter.value = p.menuItemCateringFilter ?? null;
    menuItemCateringTypeFilter.value = p.menuItemCateringTypeFilter ?? null;
  } catch {}
}

// Auto-persist filter state to localStorage on change
watch([ingredientSearch, ingredientCategoryFilter, recipeSearch, recipeCategoryFilter, recipeIngredientFilter, recipeMenuItemFilter, recipeBrandFilter, menuItemSearch, menuItemCategoryFilter, menuItemBrandFilter, menuItemCateringFilter, menuItemCateringTypeFilter], () => {
  localStorage.setItem(
    FOODCOST_FILTERS_KEY,
    JSON.stringify({
      ingredientSearch: ingredientSearch.value,
      ingredientCategoryFilter: ingredientCategoryFilter.value,
      recipeSearch: recipeSearch.value,
      recipeCategoryFilter: recipeCategoryFilter.value,
      recipeIngredientFilter: recipeIngredientFilter.value,
      recipeMenuItemFilter: recipeMenuItemFilter.value,
      recipeBrandFilter: recipeBrandFilter.value,
      menuItemSearch: menuItemSearch.value,
      menuItemCategoryFilter: menuItemCategoryFilter.value,
      menuItemBrandFilter: menuItemBrandFilter.value,
      menuItemCateringFilter: menuItemCateringFilter.value,
      menuItemCateringTypeFilter: menuItemCateringTypeFilter.value,
    }),
  );
}, { deep: true });

// ── Clear filters ─────────────────────────────────────────────────────────────

function clearFilters() {
  ingredientSearch.value = "";
  ingredientCategoryFilter.value = null;
  recipeSearch.value = "";
  recipeCategoryFilter.value = null;
  recipeIngredientFilter.value = null;
  recipeMenuItemFilter.value = null;
  recipeBrandFilter.value = null;
  menuItemSearch.value = "";
  menuItemCategoryFilter.value = null;
  menuItemBrandFilter.value = null;
  menuItemCateringFilter.value = null;
  menuItemCateringTypeFilter.value = null;
  localStorage.removeItem(FOODCOST_FILTERS_KEY);
}

// Packaging
const packagingItems = ref([]);
const packagingLoading = ref(false);
const pkgMenuItemsDialogOpen = ref(false);
const pkgMenuItemsDialogTitle = ref("");
const pkgMenuItemsDialogList = ref([]);
const pkgMenuItemsDialogLoading = ref(false);

// Shared suppliers (from address-book via food-cost API)
const sharedSuppliers = ref([]);

// Sales options (platforms, brands, locations as {id,name} objects, rules from sales DB business rules)
const salesOptions = ref({ platforms: [], brands: [], locations: [], rules: [] });

// Images dialog
const imagesDialogOpen = ref(false);
const imagesDialogItem = ref(null);
const imagesDialogImages = ref([]);

async function openImagesDialog(item) {
  imagesDialogItem.value = item;
  imagesDialogImages.value = [];
  imagesDialogOpen.value = true;
  try {
    const detail = await fetchMenuItemDetail(item.id);
    imagesDialogImages.value = detail.images || [];
  } catch {
    // keep empty
  }
}

// Selling prices for the currently selected menu item
const sellingPrices = ref([]);

// Image assets (from assets DB via food-cost API) for image picker
const imageAssets = ref([]);

// Asset tags (from assets DB via food-cost API) for filtering image assets
const assetTags = ref([]);

// Combos
const combos = ref([]);
const comboLoading = ref(false);

// Dashboard
const dashboard = ref(null);
const dashboardLoading = ref(false);

// Dialogs
const ingredientDialogOpen = ref(false);
const ingredientDialogMode = ref("create"); // create | edit
const selectedIngredient = ref(null);
const recipeDialogOpen = ref(false);
const recipeDialogMode = ref("create");
const selectedRecipe = ref(null);
const menuItemDialogOpen = ref(false);
const menuItemDialogMode = ref("create"); // create | edit
const selectedMenuItem = ref(null);
const menuItemFilterDialogOpen = ref(false);
const recipeImportDialogOpen = ref(false);
const ingredientImportDialogOpen = ref(false);
// Combo dialog
const comboDialogOpen = ref(false);
const comboDialogMode = ref("create"); // create | edit | view
const selectedCombo = ref(null);
// Trigger for opening category create dialog from dots menu
const categoryCreateDialogOpen = ref(false);

// Snackbar
const snackbar = ref(false);
const snackbarText = ref("");
const snackbarColor = ref("success");

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Show a snackbar notification */
function showSnackbar(text, color = "success") {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
}

// ── API Calls ───────────────────────────────────────────────────────────────

/** Fetch all ingredients with current filters */
async function fetchIngredients() {
  ingredientLoading.value = true;
  try {
    const params = {};
    if (ingredientSearch.value) params.search = ingredientSearch.value;
    if (ingredientCategoryFilter.value) params.category = ingredientCategoryFilter.value;
    const { data } = await menuApi.get("/api/menu/ingredients", { params });
    ingredients.value = Array.isArray(data) ? data : [];
    // Keep unfiltered list in sync when no search filter is active
    if (!ingredientSearch.value && !ingredientCategoryFilter.value) allIngredients.value = data;
  } catch (err) {
    console.error("Failed to fetch ingredients:", err);
    showSnackbar("Failed to load ingredients", "error");
  } finally {
    ingredientLoading.value = false;
  }
}

/** Fetch ingredient categories */
async function fetchIngredientCategories() {
  try {
    const { data } = await menuApi.get("/api/menu/item-categories");
    ingredientCategories.value = data;
  } catch (err) {
    console.error("Failed to fetch item categories:", err);
  }
}

/** Create a new ingredient */
async function createIngredientApi(ingredientData) {
  const { data } = await menuApi.post("/api/menu/ingredients", ingredientData);
  await Promise.all([fetchIngredients(), fetchAllIngredients()]);
  showSnackbar(`Ingredient "${data.name}" created`);
  return data;
}

/** Update an existing ingredient */
async function updateIngredientApi(id, ingredientData) {
  const { data } = await menuApi.put(`/api/menu/ingredients/${id}`, ingredientData);
  await Promise.all([fetchIngredients(), fetchAllIngredients()]);
  showSnackbar(`Ingredient "${data.name}" updated`);
  return data;
}

/** Fetch recipes that use a specific ingredient */
async function fetchIngredientRecipes(ingredientId) {
  const { data } = await menuApi.get(`/api/menu/ingredients/${ingredientId}/recipes`);
  return data;
}

/** Delete an ingredient */
async function deleteIngredientApi(id) {
  await menuApi.delete(`/api/menu/ingredients/${id}`);
  await Promise.all([fetchIngredients(), fetchAllIngredients()]);
  showSnackbar("Ingredient deleted");
}

/** Fetch all recipes with current filters */
async function fetchRecipes() {
  recipeLoading.value = true;
  try {
    const params = {};
    if (recipeSearch.value) params.search = recipeSearch.value;
    if (recipeIngredientFilter.value) params.ingredientId = recipeIngredientFilter.value;
    if (recipeMenuItemFilter.value) params.menuItemId = recipeMenuItemFilter.value;
    if (recipeBrandFilter.value) params.brandId = recipeBrandFilter.value;
    const { data } = await menuApi.get("/api/menu/recipes", { params });
    recipes.value = Array.isArray(data) ? data : [];
    // Keep unfiltered list in sync when no filters are active
    if (!recipeSearch.value && !recipeIngredientFilter.value && !recipeBrandFilter.value) allRecipes.value = data;
  } catch (err) {
    console.error("Failed to fetch recipes:", err);
    showSnackbar("Failed to load recipes", "error");
  } finally {
    recipeLoading.value = false;
  }
}

/** Fetch all recipes without filters — used by sub-recipe picker dropdown */
/** Fetch ALL ingredients without any search/filter — used by dialogs that need the full list */
async function fetchAllIngredients() {
  try {
    const { data } = await menuApi.get("/api/menu/ingredients");
    allIngredients.value = data;
  } catch (err) {
    console.error("Failed to fetch all ingredients:", err);
  }
}

async function fetchAllRecipes() {
  try {
    const { data } = await menuApi.get("/api/menu/recipes");
    allRecipes.value = data;
  } catch (err) {
    console.error("Failed to fetch all recipes:", err);
  }
}

/** Fetch recipe categories */
async function fetchRecipeCategories() {
  try {
    const { data } = await menuApi.get("/api/menu/recipe-categories");
    recipeCategories.value = data;
  } catch (err) {
    console.error("Failed to fetch recipe categories:", err);
  }
}

/** Fetch a single recipe with ingredient breakdown */
async function fetchRecipeDetail(id) {
  const { data } = await menuApi.get(`/api/menu/recipes/${id}`);
  return data;
}

/** Create a new recipe */
async function createRecipeApi(recipeData) {
  const { data } = await menuApi.post("/api/menu/recipes", recipeData);
  await Promise.all([fetchRecipes(), fetchAllRecipes()]);
  showSnackbar(`Recipe "${data.name}" created`);
  return data;
}

/** Update an existing recipe */
async function updateRecipeApi(id, recipeData) {
  const { data } = await menuApi.put(`/api/menu/recipes/${id}`, recipeData);
  await Promise.all([fetchRecipes(), fetchAllRecipes()]);
  showSnackbar(`Recipe "${data.name}" updated`);
  return data;
}

/** Delete a recipe */
async function deleteRecipeApi(id) {
  await menuApi.delete(`/api/menu/recipes/${id}`);
  await Promise.all([fetchRecipes(), fetchAllRecipes()]);
  showSnackbar("Recipe deleted");
}

/** Fetch dashboard summary stats */
async function fetchDashboard() {
  dashboardLoading.value = true;
  try {
    const { data } = await menuApi.get("/api/menu/dashboard");
    dashboard.value = data;
  } catch (err) {
    console.error("Failed to fetch dashboard:", err);
    showSnackbar("Failed to load dashboard", "error");
  } finally {
    dashboardLoading.value = false;
  }
}

/** Import ingredients from Excel file */
async function importIngredientsFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await menuApi.post("/api/menu/import/ingredients", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  await fetchIngredients();
  await fetchIngredientCategories();
  return data;
}

/** Import recipes from Excel file */
async function importRecipesFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await menuApi.post("/api/menu/import/recipes", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  await fetchRecipes();
  return data;
}

// ── Combos API ──────────────────────────────────────────────────────────────

/** Fetch all combos */
async function fetchCombos() {
  comboLoading.value = true;
  try {
    const { data } = await menuApi.get("/api/menu/combos");
    combos.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("Failed to fetch combos:", err);
    showSnackbar("Failed to load combos", "error");
  } finally {
    comboLoading.value = false;
  }
}

/** Fetch a single combo with component breakdown */
async function fetchComboDetail(id) {
  const { data } = await menuApi.get(`/api/menu/combos/${id}`);
  return data;
}

/** Create a new combo */
async function createComboApi(comboData) {
  const { data } = await menuApi.post("/api/menu/combos", comboData);
  await fetchCombos();
  showSnackbar(`Combo "${data.name}" created`);
  return data;
}

/** Update an existing combo */
async function updateComboApi(id, comboData) {
  const { data } = await menuApi.put(`/api/menu/combos/${id}`, comboData);
  await fetchCombos();
  showSnackbar(`Combo "${data.name}" updated`);
  return data;
}

/** Delete a combo */
async function deleteComboApi(id) {
  await menuApi.delete(`/api/menu/combos/${id}`);
  await fetchCombos();
  showSnackbar("Combo deleted");
}

// ── Menu Items API ──────────────────────────────────────────────────────────

/** Fetch all menu items with current filters */
// Recipe → Menu Items dialog
const recipeMenuItemsOpen = ref(false);
const recipeMenuItemsTitle = ref("");
const recipeMenuItemsList = ref([]);

async function openRecipeMenuItems(recipe) {
  recipeMenuItemsTitle.value = recipe.name;
  recipeMenuItemsList.value = [];
  recipeMenuItemsOpen.value = true;
  try {
    const { data } = await menuApi.get("/api/menu/menu-items", { params: { recipeId: recipe.id } });
    recipeMenuItemsList.value = Array.isArray(data) ? data : [];
  } catch {
    // keep empty
  }
}

function openMenuItemFromRecipeDialog(mi) {
  recipeMenuItemsOpen.value = false;
  selectedMenuItem.value = { ...mi };
  menuItemDialogMode.value = "view";
  menuItemDialogOpen.value = true;
}

async function fetchAllMenuItems() {
  try {
    const { data } = await menuApi.get("/api/menu/menu-items");
    allMenuItems.value = Array.isArray(data) ? data : [];
  } catch { /* keep previous list */ }
}

async function fetchMenuItems() {
  menuItemLoading.value = true;
  try {
    const params = {};
    if (menuItemSearch.value) params.search = menuItemSearch.value;
    if (menuItemCategoryFilter.value) params.category = menuItemCategoryFilter.value;
    if (menuItemBrandFilter.value) params.brand = menuItemBrandFilter.value;
    if (menuItemCateringFilter.value != null) params.catering = menuItemCateringFilter.value ? "1" : "0";
    if (menuItemCateringTypeFilter.value) params.cateringType = menuItemCateringTypeFilter.value;
    const { data } = await menuApi.get("/api/menu/menu-items", { params });
    menuItems.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("Failed to fetch menu items:", err);
    showSnackbar("Failed to load menu items", "error");
  } finally {
    menuItemLoading.value = false;
  }
}

// Debounced refetch when menu item filters change
let _miTimer;
watch(menuItemSearch, () => { clearTimeout(_miTimer); _miTimer = setTimeout(fetchMenuItems, 300); });
watch(menuItemCategoryFilter, () => fetchMenuItems());
watch(menuItemBrandFilter, () => fetchMenuItems());
watch(menuItemCateringFilter, () => fetchMenuItems());
watch(menuItemCateringTypeFilter, () => fetchMenuItems());

/** Fetch a single menu item with packaging breakdown */
async function fetchMenuItemDetail(id) {
  const { data } = await menuApi.get(`/api/menu/menu-items/${id}`);
  return data;
}

/**
 * Fetch all menu items with their selling prices for PDF export.
 * Fetches detail for each item in parallel to get selling_prices arrays.
 * @returns {Array} Menu items enriched with selling_prices
 */
/**
 * The listed menu items (filters applied) as a PDF without any price or cost — e.g. the
 * catering list for a client (2026-10-30). Titled "Catering menu" when the Catering filter
 * is on; the other filters are printed under the title.
 */
async function exportMenuItemsNoPrices() {
  if (!menuItems.value.length) {
    showSnackbar("No menu items to export", "warning");
    return;
  }
  const filters = [];
  if (menuItemSearch.value) filters.push(`Search: ${menuItemSearch.value}`);
  if (menuItemCategoryFilter.value) filters.push(`Category: ${menuItemCategoryFilter.value}`);
  if (menuItemBrandFilter.value) {
    filters.push(`Brand: ${salesOptions.value.brands.find((b) => b.id === menuItemBrandFilter.value)?.name || menuItemBrandFilter.value}`);
  }
  if (menuItemCateringFilter.value === false) filters.push("Not for catering");
  if (menuItemCateringTypeFilter.value) filters.push(`Catering type: ${menuItemCateringTypeFilter.value}`);
  const catering = menuItemCateringFilter.value === true || !!menuItemCateringTypeFilter.value;
  const title = catering ? "Catering menu" : "Menu items";
  try {
    // A catering menu is grouped by live station / buffet, the rest by menu category
    await exportMenuItemsNoPricesPdf(menuItems.value, {
      title, filters,
      groupBy: catering ? { types: menuItemCateringTypeFilter.value ? [menuItemCateringTypeFilter.value] : CATERING_TYPES } : null,
    });
  } catch {
    showSnackbar("Failed to generate PDF", "error");
  }
}

async function fetchMenuItemsWithPrices() {
  // Use current menuItems list; fetch detail for each to get selling_prices
  const items = menuItems.value;
  const details = await Promise.all(items.map((item) => fetchMenuItemDetail(item.id)));
  return details;
}

/** Create a new menu item */
async function createMenuItemApi(data) {
  const { data: result } = await menuApi.post("/api/menu/menu-items", data);
  await Promise.all([fetchMenuItems(), fetchAllMenuItems()]);
  showSnackbar(`Menu item "${result.name}" created`);
  return result;
}

/** Update an existing menu item */
async function updateMenuItemApi(id, data) {
  const { data: result } = await menuApi.put(`/api/menu/menu-items/${id}`, data);
  await Promise.all([fetchMenuItems(), fetchAllMenuItems()]);
  showSnackbar(`Menu item "${result.name}" updated`);
  return result;
}

/** Delete a menu item */
async function deleteMenuItemApi(id) {
  await menuApi.delete(`/api/menu/menu-items/${id}`);
  await Promise.all([fetchMenuItems(), fetchAllMenuItems()]);
  showSnackbar("Menu item deleted");
}

// ── Packaging API ───────────────────────────────────────────────────────────

/** Fetch packaging ingredients */
async function fetchPackaging() {
  packagingLoading.value = true;
  try {
    const { data } = await menuApi.get("/api/menu/packaging");
    packagingItems.value = data;
  } catch (err) {
    console.error("Failed to fetch packaging:", err);
    showSnackbar("Failed to load packaging", "error");
  } finally {
    packagingLoading.value = false;
  }
}

/** Create a new packaging item */
async function createPackagingApi(packagingData) {
  const { data } = await menuApi.post("/api/menu/packaging", packagingData);
  await fetchPackaging();
  showSnackbar(`Packaging "${data.name}" created`);
  return data;
}

/** Update a packaging item */
async function updatePackagingApi(id, data) {
  const { data: result } = await menuApi.put(`/api/menu/packaging/${id}`, data);
  await fetchPackaging();
  showSnackbar(`Packaging "${result.name}" updated`);
  return result;
}

/** Delete a packaging item */
async function deletePackagingApi(id) {
  await menuApi.delete(`/api/menu/packaging/${id}`);
  await fetchPackaging();
  showSnackbar("Packaging item deleted");
}

/** Fetch menu items that use a specific packaging item */
async function fetchPackagingMenuItems(packagingId) {
  const { data } = await menuApi.get(`/api/menu/packaging/${packagingId}/menu-items`);
  return data;
}

async function openPkgMenuItems(pkg) {
  pkgMenuItemsDialogTitle.value = `Menu items using ${pkg.name}`;
  pkgMenuItemsDialogList.value = [];
  pkgMenuItemsDialogLoading.value = true;
  pkgMenuItemsDialogOpen.value = true;
  try {
    const data = await fetchPackagingMenuItems(pkg.id);
    pkgMenuItemsDialogList.value = Array.isArray(data) ? data : [];
  } catch {
    // keep empty
  } finally {
    pkgMenuItemsDialogLoading.value = false;
  }
}

async function openMenuItemFromPkgDialog(mi) {
  pkgMenuItemsDialogOpen.value = false;
  const detail = await fetchMenuItemDetail(mi.id);
  selectedMenuItem.value = detail;
  menuItemDialogMode.value = "view";
  menuItemDialogOpen.value = true;
}

// ── Selling Prices API ──────────────────────────────────────────────────

/** Fetch selling prices for a menu item */
async function fetchSellingPrices(menuItemId) {
  try {
    const { data } = await menuApi.get(`/api/menu/menu-items/${menuItemId}/selling-prices`);
    sellingPrices.value = data;
    return data;
  } catch (err) {
    console.error("Failed to fetch selling prices:", err);
    return [];
  }
}

/** Create a selling price */
async function createSellingPriceApi(menuItemId, priceData) {
  const { data } = await menuApi.post(`/api/menu/menu-items/${menuItemId}/selling-prices`, priceData);
  await fetchSellingPrices(menuItemId);
  return data;
}

/** Update a selling price */
async function updateSellingPriceApi(id, priceData, menuItemId) {
  const { data } = await menuApi.put(`/api/menu/selling-prices/${id}`, priceData);
  if (menuItemId) await fetchSellingPrices(menuItemId);
  return data;
}

/** Delete a selling price */
async function deleteSellingPriceApi(id, menuItemId) {
  await menuApi.delete(`/api/menu/selling-prices/${id}`);
  if (menuItemId) await fetchSellingPrices(menuItemId);
}

/** Fetch image assets, optionally filtered by tag IDs */
async function fetchImageAssets(tagIds) {
  try {
    const params = {};
    if (tagIds?.length > 0) params.tags = tagIds.join(",");
    const { data } = await menuApi.get("/api/menu/image-assets", { params });
    imageAssets.value = data;
  } catch (err) {
    console.error("Failed to fetch image assets:", err);
  }
}

/** Fetch asset tags for image filtering */
async function fetchAssetTags() {
  try {
    const { data } = await menuApi.get("/api/menu/asset-tags");
    assetTags.value = data;
  } catch (err) {
    console.error("Failed to fetch asset tags:", err);
  }
}

/** Save menu item images (array of {platform_id, brand_id, location_id, asset_id}) */
async function saveMenuItemImagesApi(menuItemId, images) {
  await menuApi.post(`/api/menu/menu-items/${menuItemId}/images`, { images });
}

/** Fetch sales options (platforms, brands, locations from sales DB) */
async function fetchSalesOptions() {
  try {
    const { data } = await menuApi.get("/api/menu/sales-options");
    salesOptions.value = data;
  } catch (err) {
    console.error("Failed to fetch sales options:", err);
  }
}

/**
 * Return filtered dropdown options based on current selections.
 * Each dropdown is filtered by the OTHER dimensions (not itself),
 * matching the cascading pattern used in campaigns and reviews.
 * All params and returns use IDs; returns {id, name} objects.
 */
function getFilteredSalesOptions(platformId, brandId, locationId) {
  const rules = salesOptions.value.rules;
  if (!rules.length) {
    return {
      platforms: salesOptions.value.platforms,
      brands: salesOptions.value.brands,
      locations: salesOptions.value.locations,
    };
  }

  // Platforms: filter by brand + location
  let rulesForPlatforms = rules;
  if (brandId) rulesForPlatforms = rulesForPlatforms.filter((r) => r.brand_id === brandId);
  if (locationId) rulesForPlatforms = rulesForPlatforms.filter((r) => r.location_id === locationId);
  const platforms = uniqueById(rulesForPlatforms, "platform_id", "platform");

  // Brands: filter by platform + location
  let rulesForBrands = rules;
  if (platformId) rulesForBrands = rulesForBrands.filter((r) => r.platform_id === platformId);
  if (locationId) rulesForBrands = rulesForBrands.filter((r) => r.location_id === locationId);
  const brands = uniqueById(rulesForBrands, "brand_id", "brand");

  // Locations: filter by platform + brand
  let rulesForLocations = rules;
  if (platformId) rulesForLocations = rulesForLocations.filter((r) => r.platform_id === platformId);
  if (brandId) rulesForLocations = rulesForLocations.filter((r) => r.brand_id === brandId);
  const locations = uniqueById(rulesForLocations, "location_id", "location");

  return { platforms, brands, locations };
}

/** Extract unique {id, name} objects from an array of rules */
function uniqueById(rules, idKey, nameKey) {
  const seen = new Map();
  for (const r of rules) {
    if (!seen.has(r[idKey])) seen.set(r[idKey], { id: r[idKey], name: r[nameKey] });
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Fetch shared suppliers from address-book (via food-cost proxy) */
async function fetchSharedSuppliers() {
  try {
    const { data } = await menuApi.get("/api/menu/suppliers");
    sharedSuppliers.value = data;
  } catch (err) {
    console.error("Failed to fetch shared suppliers:", err);
  }
}

// ── Recipe Procedure API ────────────────────────────────────────────────────

/** Save a recipe's procedure (steps + how_to_serve + how_to_assemble) */
async function saveRecipeProcedureApi(id, { steps, how_to_serve, how_to_assemble }) {
  const { data } = await menuApi.put(`/api/menu/recipes/${id}/procedure`, {
    steps, how_to_serve, how_to_assemble,
  });
  showSnackbar("Procedure saved");
  return data;
}

// ── Dialog Helpers ───────────────────────────────────────────────────────────

/** Open the ingredient dialog in create mode */
function openCreateIngredient() {
  selectedIngredient.value = null;
  ingredientDialogMode.value = "create";
  ingredientDialogOpen.value = true;
}

/** Open the ingredient dialog in edit mode */
function openEditIngredient(ingredient) {
  selectedIngredient.value = { ...ingredient };
  ingredientDialogMode.value = "edit";
  ingredientDialogOpen.value = true;
}

/** Open the recipe dialog in create mode */
function openCreateRecipe() {
  selectedRecipe.value = null;
  recipeDialogMode.value = "create";
  recipeDialogOpen.value = true;
}

/** Open the recipe dialog in edit mode */
function openEditRecipe(recipe) {
  selectedRecipe.value = { ...recipe };
  recipeDialogMode.value = "edit";
  recipeDialogOpen.value = true;
}

/** Open the recipe dialog in view (readonly) mode */
function openViewRecipe(recipe) {
  selectedRecipe.value = { ...recipe };
  recipeDialogMode.value = "view";
  recipeDialogOpen.value = true;
}

/** Open the combo dialog in create mode */
function openCreateCombo() {
  selectedCombo.value = null;
  comboDialogMode.value = "create";
  comboDialogOpen.value = true;
}

/** Open the combo dialog in edit mode */
function openEditCombo(combo) {
  selectedCombo.value = { ...combo };
  comboDialogMode.value = "edit";
  comboDialogOpen.value = true;
}

/** Open the combo dialog in view mode */
function openViewCombo(combo) {
  selectedCombo.value = { ...combo };
  comboDialogMode.value = "view";
  comboDialogOpen.value = true;
}

/** Open the menu item dialog in create mode */
function openCreateMenuItem() {
  selectedMenuItem.value = null;
  menuItemDialogMode.value = "create";
  menuItemDialogOpen.value = true;
}

/** Open the menu item dialog in edit mode */
function openEditMenuItem(item) {
  selectedMenuItem.value = { ...item };
  menuItemDialogMode.value = "edit";
  menuItemDialogOpen.value = true;
}

/** Open the menu item dialog in view (readonly) mode */
function openViewMenuItem(item) {
  selectedMenuItem.value = { ...item };
  menuItemDialogMode.value = "view";
  menuItemDialogOpen.value = true;
}

// ── Role Helpers ────────────────────────────────────────────────────────────

/** Check if user is admin or appadmin */
function isAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return ["admin", "appadmin"].includes(user.role);
  } catch {
    return false;
  }
}

// ── Composable Export ───────────────────────────────────────────────────────

export function useFoodCost() {
  return {
    // Ingredients
    ingredients,
    ingredientCategories,
    ingredientSearch,
    ingredientCategoryFilter,
    ingredientLoading,
    fetchIngredients,
    fetchIngredientCategories,
    createIngredientApi,
    updateIngredientApi,
    deleteIngredientApi,
    fetchIngredientRecipes,
    importIngredientsFile,

    // Recipes
    recipes,
    allIngredients,
    allRecipes,
    recipeCategories,
    recipeSearch,
    recipeCategoryFilter,
    recipeIngredientFilter,
    recipeMenuItemFilter,
    recipeBrandFilter,
    recipeLoading,
    fetchRecipes,
    fetchAllIngredients,
    fetchAllRecipes,
    fetchRecipeCategories,
    fetchRecipeDetail,
    createRecipeApi,
    updateRecipeApi,
    deleteRecipeApi,
    importRecipesFile,
    saveRecipeProcedureApi,

    // Combos
    combos,
    comboLoading,
    fetchCombos,
    fetchComboDetail,
    createComboApi,
    updateComboApi,
    deleteComboApi,
    comboDialogOpen,
    comboDialogMode,
    selectedCombo,
    openCreateCombo,
    openEditCombo,
    openViewCombo,

    // Menu Items
    menuItems,
    allMenuItems,
    fetchAllMenuItems,
    menuItemSearch,
    menuItemCategoryFilter,
    menuItemBrandFilter,
    menuItemCateringFilter,
    menuItemCateringTypeFilter,
    menuItemLoading,
    fetchMenuItems,
    fetchMenuItemDetail,
    fetchMenuItemsWithPrices,
    exportMenuItemsNoPrices,
    createMenuItemApi,
    updateMenuItemApi,
    deleteMenuItemApi,

    // Packaging
    packagingItems,
    packagingLoading,
    fetchPackaging,
    createPackagingApi,
    updatePackagingApi,
    deletePackagingApi,
    fetchPackagingMenuItems,
    pkgMenuItemsDialogOpen,
    pkgMenuItemsDialogTitle,
    pkgMenuItemsDialogList,
    pkgMenuItemsDialogLoading,
    openPkgMenuItems,
    openMenuItemFromPkgDialog,
    recipeMenuItemsOpen,
    recipeMenuItemsTitle,
    recipeMenuItemsList,
    openRecipeMenuItems,
    openMenuItemFromRecipeDialog,

    // Selling Prices
    imagesDialogOpen,
    imagesDialogItem,
    imagesDialogImages,
    openImagesDialog,
    sellingPrices,

    // Image assets & tags
    imageAssets,
    assetTags,
    fetchImageAssets,
    fetchAssetTags,
    saveMenuItemImagesApi,
    fetchSellingPrices,
    createSellingPriceApi,
    updateSellingPriceApi,
    deleteSellingPriceApi,

    // Sales options (platforms, brands, locations, rules)
    salesOptions,
    fetchSalesOptions,
    getFilteredSalesOptions,

    // Shared suppliers
    sharedSuppliers,
    fetchSharedSuppliers,

    // Dashboard
    dashboard,
    dashboardLoading,
    fetchDashboard,

    // Dialogs
    ingredientDialogOpen,
    ingredientDialogMode,
    selectedIngredient,
    recipeDialogOpen,
    recipeDialogMode,
    selectedRecipe,
    menuItemDialogOpen,
    menuItemDialogMode,
    selectedMenuItem,
    openCreateIngredient,
    openEditIngredient,
    openCreateRecipe,
    openEditRecipe,
    openViewRecipe,
    menuItemFilterDialogOpen,
    recipeImportDialogOpen,
    ingredientImportDialogOpen,
    categoryCreateDialogOpen,
    openCreateMenuItem,
    openEditMenuItem,
    openViewMenuItem,

    // Snackbar
    snackbar,
    snackbarText,
    snackbarColor,
    showSnackbar,

    // Filters
    clearFilters,

    // Roles
    isAdmin,
  };
}
